/** @jsx jsx */
import React, { useCallback, useRef } from 'react';
import { jsx } from '@compiled/react';
import ArrowLeftIcon from '@atlaskit/icon/core/migration/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/migration/arrow-right';
import { Box, Flex, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import CloseIcon from '@atlaskit/icon/core/migration/close--cross';
import { IconButton } from '@atlaskit/button/new';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { fg } from '@atlassian/jira-feature-gating';
import HelpPanelLink from '@atlassian/jira-help-panel-button/src/index.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import Button from '@atlassian/jira-portfolio-3-common/src/button/index.tsx';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types.tsx';
import {
	type WarningMessages,
	WarningTypes,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command/warnings/engine.tsx';
import type { Issue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/issues/types.tsx';
import { WARNING_TYPE_NUMBER_MAP } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/warnings/types.tsx';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda/index.tsx';
import { IPH_IDS } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant.tsx';
import DocLink from '@atlassian/jira-portfolio-3-portfolio/src/common/view/doc-link/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';

import IssueCell from '../issue-link/index.tsx';
import messages from './messages.tsx';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types.tsx';

const Content = ({
	warnings,
	toggleWarningByType,
	disabledWarnings,
	issuesById,
	setSearchQuery,
	issueTypesById,
	projectsById,
	typeId,
	onTypeIdChange,
	showWarning,
	toggleShowWarning,
	focusRef,
	closePopup,
}: Props) => {
	const selectedWarningKeyRef = useRef<string | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const getOnIssueItemClick = (issue: Issue) => () => {
		const [actionSubject, action] =
			PRODUCT_ANALYTICS_EVENT_NAMES.LOCATED_WARNING_ISSUE_ITEM.split(' ');
		const analyticsEvent = createAnalyticsEvent({ action, actionSubject });
		const warningType = typeof typeId === 'string' ? WARNING_TYPE_NUMBER_MAP[typeId] || null : null;
		fireUIAnalytics(analyticsEvent, {
			warningType,
		});
		setSearchQuery(issue.summary);
	};

	const getOnToggleWarningByType = (key: string, nextState: boolean) => () => {
		const [actionSubject, action] = PRODUCT_ANALYTICS_EVENT_NAMES.TOGGLED_WARNING_TYPE.split(' ');
		const analyticsEvent = createAnalyticsEvent({ action, actionSubject });

		const warningType = WARNING_TYPE_NUMBER_MAP[key] || null;
		fireUIAnalytics(analyticsEvent, {
			selectedOption: nextState ? 1 : 0,
			warningType,
		});
		toggleWarningByType(key);
	};

	// TODO: To be removed with FG m1_features_page_experiment cleanup
	const getWarningDialogContent = () => (
		<Box as={fg('plan_a11y_fixes_jtran2') ? 'fieldset' : 'div'}>
			<Box as={fg('plan_a11y_fixes_jtran2') ? 'legend' : 'div'} xcss={warningsDialogHeaderStyles}>
				<FormattedMessage {...messages.warningsDialogHeader} />
			</Box>
			<Stack space="space.100">
				{Object.entries(WarningTypes).map(([key, value]: [string, WarningMessages]) => {
					const { header } = value;
					const isEnabled = !(disabledWarnings || []).includes(key);
					const issueCount = (warnings[key] && warnings[key].length) || 0;

					return (
						<Inline key={key} spread="space-between">
							<Box xcss={warningToggleAndTextStyles}>
								<Toggle
									isChecked={isEnabled}
									onChange={getOnToggleWarningByType(key, !isEnabled)}
									testId={`portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.content.toggle-warning-type--${key}`}
									label={formatMessage(header)}
								/>
								<FormattedMessage {...header} />
							</Box>
							<Box>
								{isEnabled && (
									<Box>
										{issueCount === 0 ? (
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
											<Button appearance="link" isDisabled className={styles.disabledWarningButton}>
												<FormattedMessage {...messages.zeroCountWarningText} />
											</Button>
										) : (
											<Button
												onClick={() => {
													onTypeIdChange(key);
													selectedWarningKeyRef.current = key;
												}}
												appearance="link"
												isDisabled={issueCount === 0}
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
												className={styles.affectedIssuesCount}
												iconAfter={<ArrowRightIcon spacing="spacious" label="" />}
												ref={
													selectedWarningKeyRef.current === key &&
													fg('plans_a11y_improvements_axolotl')
														? focusRef
														: null
												}
												{...(fg('jfp_a11y_team_timeline_warnings_no_descriptive_btn') && {
													ariaLabel: formatMessage(messages.warningCountTextIssueLabel, {
														count: issueCount,
														text: formatMessage(header),
													}),
													testId: `portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.content.warning-count-text-issue--${key}`,
												})}
											>
												<FormattedMessage
													{...(fg('jira-issue-terminology-refresh-m3')
														? messages.warningCountTextIssueTermRefresh
														: messages.warningCountText)}
													values={{
														count: issueCount,
													}}
												/>
											</Button>
										)}
									</Box>
								)}
								{!isEnabled && (
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									<Button appearance="link" isDisabled className={styles.disabledWarningButton}>
										<FormattedMessage {...messages.disabledIssueStatusText} />
									</Button>
								)}
							</Box>
						</Inline>
					);
				})}
			</Stack>
		</Box>
	);

	const renderWithFieldset = () => (
		<Box as="fieldset" xcss={warningsDialogFieldsetStyles}>
			<legend>
				<VisuallyHidden>
					<FormattedMessage {...messages.warningsDialogHeader} />
				</VisuallyHidden>
			</legend>
			{Object.entries(WarningTypes).map(([key, value]: [string, WarningMessages]) => {
				const { header } = value;
				const isEnabled = !(disabledWarnings || []).includes(key);
				const issueCount = (warnings[key] && warnings[key].length) || 0;

				return (
					<Inline key={key} spread="space-between">
						<Box xcss={warningTextStyles}>
							<Text>{formatMessage(header)}</Text>
							{isEnabled && issueCount > 0 ? (
								<Button
									appearance="link"
									iconAfter={<ArrowRightIcon spacing="spacious" label="" />}
									onClick={() => {
										onTypeIdChange(key);
										selectedWarningKeyRef.current = key;
									}}
									ref={
										selectedWarningKeyRef.current === key && fg('plans_a11y_improvements_axolotl')
											? focusRef
											: null
									}
									{...(fg('jfp_a11y_team_timeline_warnings_no_descriptive_btn') && {
										ariaLabel: formatMessage(messages.warningCountTextIssueLabel, {
											count: issueCount,
											text: formatMessage(header),
										}),
										testId: `portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.content.warning-count-text-issue--${key}`,
									})}
								>
									{formatMessage(messages.warningCountTextIssueTermRefresh, { count: issueCount })}
								</Button>
							) : null}
						</Box>
						<Box xcss={warningToggleAndTextStyles}>
							<Toggle
								isChecked={isEnabled}
								testId={`portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.content.toggle-warning-type--${key}`}
								label={formatMessage(header)}
								onChange={getOnToggleWarningByType(key, !isEnabled)}
							/>
						</Box>
					</Inline>
				);
			})}
		</Box>
	);

	const renderWithoutFieldset = () => (
		<Stack space="space.100">
			{Object.entries(WarningTypes).map(([key, value]: [string, WarningMessages]) => {
				const { header } = value;
				const isEnabled = !(disabledWarnings || []).includes(key);
				const issueCount = (warnings[key] && warnings[key].length) || 0;

				return (
					<Inline key={key} spread="space-between">
						<Box xcss={warningTextStyles}>
							<Text>{formatMessage(header)}</Text>
							{isEnabled && issueCount > 0 ? (
								<Button
									appearance="link"
									iconAfter={<ArrowRightIcon spacing="spacious" label="" />}
									onClick={() => {
										onTypeIdChange(key);
										selectedWarningKeyRef.current = key;
									}}
									ref={
										selectedWarningKeyRef.current === key && fg('plans_a11y_improvements_axolotl')
											? focusRef
											: null
									}
									{...(fg('jfp_a11y_team_timeline_warnings_no_descriptive_btn') && {
										ariaLabel: formatMessage(messages.warningCountTextIssueLabel, {
											count: issueCount,
											text: formatMessage(header),
										}),
										testId: `portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.content.warning-count-text-issue--${key}`,
									})}
								>
									{formatMessage(messages.warningCountTextIssueTermRefresh, { count: issueCount })}
								</Button>
							) : null}
						</Box>
						<Box xcss={warningToggleAndTextStyles}>
							<Toggle
								isChecked={isEnabled}
								testId={`portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.content.toggle-warning-type--${key}`}
								label={formatMessage(header)}
								onChange={getOnToggleWarningByType(key, !isEnabled)}
							/>
						</Box>
					</Inline>
				);
			})}
		</Stack>
	);

	const getWarningCenterContent = () =>
		fg('jfp_a11y_team_missing_grouping_of_form_controls')
			? renderWithFieldset()
			: renderWithoutFieldset();

	// eslint-disable-next-line @typescript-eslint/no-shadow
	const getTypeDefinedWarningDialogContent = (typeId: string) => (
		<Box testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.content.warning-type-modal">
			<Box xcss={issueListHeaderStyles}>
				{fg('jfp-a11y-team_autodev_header_fixes') ? (
					<Box as="h2" xcss={headingStyles}>
						<FormattedMessage {...WarningTypes[typeId].header} />
					</Box>
				) : (
					<FormattedMessage {...WarningTypes[typeId].header} />
				)}
				{expVal('m1_features_page_experiment', 'cohort', 'not-enrolled') !== 'variation' ? (
					<Box xcss={warningDescriptionStyles}>
						<FormattedMessage {...WarningTypes[typeId].description} />
					</Box>
				) : null}
			</Box>
			<Box xcss={issueListContainerStyles}>
				{(warnings[typeId] || []).map((issueId) => {
					const issue = issuesById[issueId];

					return (
						<IssueCell
							key={issueId}
							onClick={getOnIssueItemClick(issue)}
							issue={issue}
							projectKey={projectsById[issue.project.toString()].key}
							issueType={issueTypesById[issue.type.toString()]}
						/>
					);
				})}
			</Box>
		</Box>
	);

	let content = expValEquals('m1_features_page_experiment', 'cohort', 'variation')
		? getWarningCenterContent()
		: getWarningDialogContent();

	if (isDefined(typeId) && typeId !== '') {
		content = getTypeDefinedWarningDialogContent(typeId);
	}

	const handleClosePopup = useCallback(() => {
		closePopup?.(false);
	}, [closePopup]);

	const handleToggleWarning = () => {
		if (!showWarning) {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: 'toggle',
				actionSubjectId: 'toggleWarningCenter',
				attributes: {
					enabled: true,
					fromFeaturesPage: false,
				},
			});
		}
		toggleShowWarning?.();
	};

	return (
		<Box
			testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.content.warning-centre-modal"
			xcss={[
				fg('fix-warning-popup-width') ? modalContentStyles : modalContentHeightStyles,
				expValEquals('m1_features_page_experiment', 'cohort', 'variation') && containerStyles,
			]}
		>
			{(!isDefined(typeId) || typeId === '') &&
				(expValEquals('m1_features_page_experiment', 'cohort', 'variation') ? (
					<Stack space="space.100" xcss={headingContainerStyles}>
						<Flex justifyContent="space-between">
							<Heading size="medium" as="h2">
								{formatMessage(messages.warningCenterHeading)}
							</Heading>
							<IconButton
								appearance="subtle"
								spacing="compact"
								icon={CloseIcon}
								label={formatMessage(messages.closeLabel)}
								onClick={handleClosePopup}
								ref={
									selectedWarningKeyRef.current === null && fg('plans_a11y_improvements_axolotl')
										? focusRef
										: null
								}
							/>
						</Flex>
						<Text as="p">{formatMessage(messages.warningCenterDescription)}</Text>
						<Text as="p">
							<HelpPanelLink
								articleId={IPH_IDS.WARNING_CENTER}
								appearance="subtle-link"
								spacing="none"
								onClick={handleClosePopup}
							>
								{formatMessage(messages.warningCenterHelpLink)}
							</HelpPanelLink>
						</Text>
					</Stack>
				) : (
					<Box xcss={topDescriptionStyles}>
						<Box xcss={warningCenterHeaderStyles} as="h2">
							<FormattedMessage {...messages.warningCenterHeader} />
						</Box>
						<Box xcss={warningCenterIntroStyles}>
							<FormattedMessage
								{...(fg('jira-issue-terminology-refresh-m3')
									? messages.warningsDialogDescriptionAndDocumentationLinkIssueTermRefresh
									: messages.warningsDialogDescriptionAndDocumentationLink)}
								values={{
									link: (
										<HelpPanelLink
											articleId={IPH_IDS.WARNING_CENTER}
											fallbackComponent={
												<DocLink href={DocLink.URLs.warningsDialog} tagRef={focusRef}>
													{formatMessage(messages.iphLink)}
												</DocLink>
											}
											appearance="link"
											spacing="none"
											onClick={handleClosePopup}
										>
											{formatMessage(messages.iphLink)}
										</HelpPanelLink>
									),
								}}
							/>
						</Box>
						<Box xcss={toggleWrapperStyles}>
							<Toggle
								isChecked={showWarning}
								onChange={
									fg('add_events_for_old_features_page') ? handleToggleWarning : toggleShowWarning
								}
								testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.content.toggle-warnings-visibility"
								label={formatMessage(messages.showWarningsOnYourTimeline)}
							/>
							<FormattedMessage {...messages.showWarningsOnYourTimeline} />
						</Box>
					</Box>
				))}
			{isDefined(typeId) &&
				typeId !== '' &&
				(expValEquals('m1_features_page_experiment', 'cohort', 'variation') ? (
					<Box xcss={buttonContainerStyles}>
						<Button
							autoFocus // eslint-disable-line jsx-a11y/no-autofocus
							appearance="link"
							iconBefore={<ArrowLeftIcon spacing="spacious" label="" />}
							onClick={() => onTypeIdChange(null)}
							ref={fg('plans_a11y_improvements_axolotl') ? focusRef : null}
						>
							{formatMessage(messages.goBackToOverview)}
						</Button>
					</Box>
				) : (
					<Box xcss={backButtonStyles}>
						<Button
							appearance="link"
							iconBefore={<ArrowLeftIcon spacing="spacious" label="" />}
							onClick={() => onTypeIdChange(null)}
							ref={fg('plans_a11y_improvements_axolotl') ? focusRef : null}
						>
							<FormattedMessage {...messages.goBackToOverview} />
						</Button>
					</Box>
				))}
			<Box
				xcss={[
					fg('fix-warning-popup-width') ? contentBoxStyles : contentBoxStylesOld,
					expValEquals('m1_features_page_experiment', 'cohort', 'variation') &&
						contentContainerStyles,
				]}
			>
				{content}
			</Box>
		</Box>
	);
};

export default Content;

// Styles

const headingStyles = xcss({
	font: token('font.body'),
});

const warningTextStyles = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	height: '32px',
});

const warningToggleAndTextStyles = xcss({
	display: 'inline-flex',
	alignItems: 'center',
});

const warningsDialogHeaderStyles = xcss({
	fontWeight: token('font.weight.semibold'),
	paddingLeft: 'space.075',
	paddingBottom: 'space.150',
});

const warningsDialogFieldsetStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.100',
	justifyContent: 'stretch',
});

const issueListHeaderStyles = xcss({
	marginBottom: 'space.250',
});

const warningDescriptionStyles = xcss({
	color: 'color.text.subtle',
	font: token('font.body.UNSAFE_small'),
});

const issueListContainerStyles = xcss({
	maxHeight: '400px',
	overflowY: 'auto',
});

const containerStyles = xcss({
	paddingTop: 'space.200',
	paddingRight: 'space.300',
	paddingBottom: 'space.200',
	paddingLeft: 'space.300',
});

const headingContainerStyles = xcss({
	paddingBottom: 'space.200',
	marginBottom: 'space.200',
	borderBottomWidth: 'border.width.outline',
	borderBottomStyle: 'solid',
	borderBottomColor: 'color.border',
});

const modalContentHeightStyles = xcss({
	maxHeight: 'calc(100vh - 188px)',
});

const modalContentStyles = xcss({
	maxHeight: 'calc(100vh - 188px)',
	width: '595px',
});

const topDescriptionStyles = xcss({
	padding: 'space.200',
	backgroundColor: 'color.background.neutral',
});

const warningCenterHeaderStyles = xcss({
	marginBottom: 'space.150',
	font: token('font.heading.small'),
	color: 'color.text',
	marginLeft: 'space.075',
});

const warningCenterIntroStyles = xcss({
	marginBottom: 'space.100',
	marginLeft: 'space.075',
});

const toggleWrapperStyles = xcss({
	alignItems: 'center',
	display: 'flex',
});

const backButtonStyles = xcss({
	marginTop: 'space.150',
});

const buttonContainerStyles = xcss({
	marginBottom: 'space.200',
	transform: `translateX(${token('space.negative.200')})`,
});

const contentContainerStyles = xcss({
	padding: 'space.0',
});

const contentBoxStyles = xcss({
	padding: 'space.200',
});

const contentBoxStylesOld = xcss({
	padding: 'space.200',
	width: '595px',
});
